import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, X, Tag, MessageSquareQuote, ChevronLeft, Loader2, Share2, Eye, Store } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, uploadImage, imgUrl, siteUrl } from "@/lib/api";
import { errorKey } from "@/lib/i18n";
import { PostCard } from "@/components/dm/PostCard";
import { CategoryIcon, AnkaraStrip } from "@/components/dm/ui";
import { StatusShare } from "@/components/dm/StatusShare";

const field = "w-full min-h-[52px] rounded-2xl border-2 border-[#E8E2D9] focus:border-[#1E4D3B] outline-none bg-[#FFFDF9] px-4 text-base";

const Progress = ({ step }) => {
  const { t } = useApp();
  return (
    <div className="space-y-2" data-testid="create-progress">
      <div className="text-sm font-bold text-[#1E4D3B]">{t("step_of", { a: step })}</div>
      <div className="h-2.5 rounded-full bg-[#E8E2D9] overflow-hidden"><div className="h-full bg-[#F59E0B] rounded-full transition-[width] duration-300" style={{ width: `${(step / 3) * 100}%` }} /></div>
    </div>
  );
};

const Photos = ({ photos, setPhotos }) => {
  const { t } = useApp();
  const [busy, setBusy] = useState(0);
  const add = async (files) => {
    const list = [...files].slice(0, 6 - photos.length);
    setBusy(list.length);
    for (const f of list) {
      try { const path = await uploadImage(f); setPhotos((p) => [...p, path].slice(0, 6)); } catch { toast.error(t("error")); }
      setBusy((b) => b - 1);
    }
  };
  return (
    <div className="space-y-4">
      {photos.length < 6 && (
        <label data-testid="add-photos-tile" className="press flex flex-col items-center justify-center gap-3 min-h-[220px] rounded-[24px] border-2 border-dashed border-[#D97706] bg-[#FEF3C7]/50 cursor-pointer text-center p-6">
          <span className="w-20 h-20 rounded-full bg-[#F59E0B] flex items-center justify-center shadow-lg"><Camera className="w-10 h-10 text-[#291E18]" /></span>
          <span className="text-xl font-extrabold">{t("add_photos")}</span>
          <span className="text-sm text-[#6B5E55]">{t("photos_hint")}</span>
          <input data-testid="photo-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
        </label>
      )}
      <div className="grid grid-cols-3 gap-3">
        {photos.map((p, i) => (
          <div key={p} className="relative aspect-square rounded-2xl overflow-hidden" data-testid={`photo-thumb-${i}`}>
            <button type="button" onClick={() => setPhotos([p, ...photos.filter((x) => x !== p)])} className="w-full h-full"><img src={imgUrl(p)} alt="" className="w-full h-full object-cover" /></button>
            {i === 0 && <span className="absolute bottom-1.5 left-1.5 rounded-full bg-[#F59E0B] text-[#291E18] text-xs font-bold px-2 py-0.5">{t("cover")}</span>}
            <button type="button" data-testid={`photo-remove-${i}`} aria-label="Remove" onClick={() => setPhotos(photos.filter((x) => x !== p))} className="absolute top-1.5 right-1.5 w-8 h-8 rounded-full bg-[#291E18]/75 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
        ))}
        {Array.from({ length: Math.max(busy, 0) }).map((_, i) => <div key={`b${i}`} className="aspect-square rounded-2xl skeleton flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#6B5E55]" /></div>)}
      </div>
    </div>
  );
};

const CatPicker = ({ value, onChange }) => {
  const { meta, L } = useApp();
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" role="radiogroup">
      {meta.categories.map((c) => (
        <button type="button" role="radio" aria-checked={value === c.id} key={c.id} data-testid={`create-cat-${c.id}`} onClick={() => onChange(c.id)}
          className={`press flex items-center gap-2 min-h-[52px] px-3 rounded-2xl border-2 text-left text-sm font-bold ${value === c.id ? "border-[#1E4D3B] bg-[#1E4D3B] text-white" : "border-[#E8E2D9] bg-[#FFFDF9]"}`}>
          <CategoryIcon icon={c.icon} className={`w-5 h-5 shrink-0 ${value === c.id ? "text-[#F59E0B]" : "text-[#C2410C]"}`} />{L(c)}
        </button>
      ))}
    </div>
  );
};

const Success = ({ post }) => {
  const { t, L, hubOf } = useApp();
  const url = `${siteUrl()}/post/${post.id}`;
  return (
    <div className="max-w-md mx-auto px-5 py-16 text-center space-y-6 relative overflow-hidden" data-testid="create-success">
      <div className="relative w-28 h-28 mx-auto">
        {Array.from({ length: 14 }).map((_, i) => <span key={i} className="confetti absolute left-1/2 top-1/2 w-2.5 h-2.5 rounded-sm" style={{ background: ["#F59E0B", "#C2410C", "#1E4D3B"][i % 3], "--x": `${Math.cos(i) * 120}px`, "--y": `${Math.sin(i) * 120}px` }} />)}
        <svg viewBox="0 0 56 56" className="w-28 h-28"><circle cx="28" cy="28" r="25" fill="#1E4D3B" className="circle-draw" stroke="#F59E0B" strokeWidth="3" /><path d="M17 29l7 7 15-16" fill="none" stroke="#F59E0B" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" className="check-draw" /></svg>
      </div>
      <h1 className="text-3xl font-extrabold tracking-tight">{t("success_title", { city: L(hubOf(post.hub_id)) })}</h1>
      <div className="flex flex-col gap-3">
        <Link to={`/post/${post.id}`} data-testid="success-view-post" className="press min-h-[52px] rounded-full bg-[#1E4D3B] text-white font-extrabold inline-flex items-center justify-center gap-2"><Eye className="w-5 h-5" />{t("view_post")}</Link>
        <a href={`https://wa.me/?text=${encodeURIComponent(`${post.title} — ${url}`)}`} target="_blank" rel="noreferrer" data-testid="success-share-whatsapp" className="press min-h-[52px] rounded-full bg-[#128C7E] text-white font-extrabold inline-flex items-center justify-center gap-2"><Share2 className="w-5 h-5" />{t("share_whatsapp")}</a>
        <StatusShare p={post} className="min-h-[52px]" />
      </div>
    </div>
  );
};

const ShopBox = ({ f, set, hasShop }) => {
  const { t } = useApp();
  return (
    <div data-testid="shop-box" className={`rounded-[20px] border-2 p-4 space-y-3 ${f.in_shop ? "border-[#F59E0B] bg-[#FEF3C7]/40" : "border-[#E8E2D9] bg-[#FFFDF9]"}`}>
      <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
        <input data-testid="create-in-shop" type="checkbox" checked={f.in_shop} onChange={(e) => set({ in_shop: e.target.checked })} className="mt-1 w-5 h-5 accent-[#1E4D3B]" />
        <span><span className="font-extrabold inline-flex items-center gap-1.5"><Store className="w-5 h-5 text-[#C2410C]" />{t("shop_add")}</span><span className="block text-sm text-[#6B5E55]">{t("shop_add_hint")}</span></span>
      </label>
      {f.in_shop && !hasShop && (
        <div className="space-y-2" data-testid="shop-setup">
          <div className="text-sm font-bold text-[#1E4D3B]">{t("shop_first")}</div>
          <input data-testid="shop-name" value={f.shop_name} onChange={(e) => set({ shop_name: e.target.value })} maxLength={80} placeholder={t("shop_name")} className={field} />
          <input data-testid="shop-desc" value={f.shop_desc} onChange={(e) => set({ shop_desc: e.target.value })} maxLength={200} placeholder={t("shop_desc")} className={field} />
        </div>
      )}
    </div>
  );
};

const TypeChooser = ({ onPick }) => {
  const { t } = useApp();
  return (
    <div className="space-y-4">
      <h1 className="text-3xl font-extrabold tracking-tight">{t("choose_type")}</h1>
      <button data-testid="choose-listing" onClick={() => onPick("listing")} className="press w-full text-left p-6 rounded-[24px] bg-[#1E4D3B] text-white flex gap-4 items-center"><Tag className="w-10 h-10 text-[#F59E0B]" /><div><div className="text-xl font-extrabold">{t("create_listing")}</div><div className="text-white/80">{t("listing_desc")}</div></div></button>
      <button data-testid="choose-request" onClick={() => onPick("request")} className="press w-full text-left p-6 rounded-[24px] bg-[#FFF4EA] border-2 border-[#C2410C]/40 flex gap-4 items-center"><MessageSquareQuote className="w-10 h-10 text-[#C2410C]" /><div><div className="text-xl font-extrabold">{t("create_request")}</div><div className="text-[#6B5E55]">{t("request_desc")}</div></div></button>
    </div>
  );
};

export default function CreatePost() {
  const { t, user, ready, requireVerified, activeHubs, hub, L, areasOf, refreshUser } = useApp();
  const hasShop = !!user?.business_full?.enabled;
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [type, setType] = useState(params.get("type") || "");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const defaultHub = activeHubs.find((h) => h.id === (user?.city_hub_id || hub?.id))?.id || activeHubs[0]?.id;
  const [f, setF] = useState({ title: "", description: "", price: "", contact_for_price: false, category_id: "", hub_id: "", area_id: "", photos: [], status: "", in_shop: false, shop_name: "", shop_desc: "" });
  const set = (p) => setF((s) => ({ ...s, ...p }));
  const allowed = !!(ready && user?.phone_verified && !user?.is_banned);

  useEffect(() => { if (hasShop && !id) set({ in_shop: true }); }, [hasShop, id]);

  useEffect(() => { if (ready && !allowed) requireVerified(); }, [ready, allowed]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!f.hub_id && defaultHub) set({ hub_id: defaultHub }); }, [defaultHub]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!id) return;
    api.get(`/posts/${id}`).then(({ data }) => { setType(data.type); setF((s) => ({ ...s, ...data, price: data.price ?? "", area_id: data.area_id || "", in_shop: !!data.in_shop })); });
  }, [id]);

  if (!ready) return null;
  if (!allowed) return (
    <div className="max-w-md mx-auto px-5 py-16 text-center space-y-5" data-testid="create-gate">
      <AnkaraStrip className="rounded-full" />
      <h1 className="text-2xl font-extrabold">{user ? t("verify_title") : t("create_account")}</h1>
      <p>{user ? t("verify_body") : t("signup_prompt")}</p>
      <button data-testid="create-gate-button" onClick={requireVerified} className="press min-h-[52px] px-8 rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold">{user ? t("verify_now") : t("create_account")}</button>
    </div>
  );
  if (done) return <Success post={done} />;
  if (!type) return <div className="max-w-xl mx-auto px-4 py-8"><TypeChooser onPick={setType} /></div>;

  const isReq = type === "request";
  const preview = { id: "preview", type, title: f.title || t(isReq ? "title_request" : "title_listing"), description: f.description, price: f.price === "" ? null : Number(f.price),
    contact_for_price: f.contact_for_price, category_id: f.category_id, hub_id: f.hub_id, area_id: f.area_id || null, photos: f.photos,
    status: isReq ? "open" : "available", created_at: new Date().toISOString(), poster: { ...user, member_since: user.member_since } };

  const next = () => {
    if (step === 2 || (isReq && step === 1)) {
      if (!f.title.trim()) return toast.error(t("title_required"));
      if (!f.category_id) return toast.error(t("category_required"));
    }
    setStep(step + 1); window.scrollTo(0, 0);
  };
  const publish = async () => {
    const newShop = !isReq && f.in_shop && !hasShop;
    if (newShop && !f.shop_name.trim()) return toast.error(t("shop_name_required"));
    setBusy(true);
    const body = { type, title: f.title, description: f.description, price: f.price === "" || f.contact_for_price ? null : Number(f.price), contact_for_price: f.contact_for_price,
      category_id: f.category_id, hub_id: f.hub_id, area_id: f.area_id || null, photos: f.photos, status: f.status || null, in_shop: !isReq && f.in_shop };
    try {
      if (newShop) {
        await api.put("/me/business", { business_name: f.shop_name.trim(), description: f.shop_desc.trim(), service_areas: [f.hub_id], category_id: f.category_id || null });
        await refreshUser();
      }
      const { data } = id ? await api.put(`/posts/${id}`, body) : await api.post("/posts", body);
      if (id) { toast.success(t("saved")); navigate(`/post/${id}`); } else setDone(data);
    } catch (e) { toast.error(t(errorKey(e))); }
    setBusy(false);
  };

  const titleField = <input data-testid="create-title" value={f.title} onChange={(e) => set({ title: e.target.value })} maxLength={120} placeholder={t(isReq ? "title_request" : "title_listing")} className={field} />;
  const catField = <div className="space-y-2"><div className="font-bold">{t("category")}</div><CatPicker value={f.category_id} onChange={(c) => set({ category_id: c })} /></div>;
  const priceField = (
    <div className="space-y-2">
      <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-[#6B5E55]">$</span>
        <input data-testid="create-price" type="number" inputMode="decimal" min="0" disabled={f.contact_for_price} value={f.price} onChange={(e) => set({ price: e.target.value })} placeholder={`${t(isReq ? "budget" : "price")} (CAD)${isReq ? ` — ${t("optional")}` : ""}`} className={`${field} pl-9 disabled:opacity-50`} /></div>
      {!isReq && <label className="flex items-center gap-3 min-h-[44px] font-semibold cursor-pointer"><input data-testid="create-contact-for-price" type="checkbox" checked={f.contact_for_price} onChange={(e) => set({ contact_for_price: e.target.checked })} className="w-5 h-5 accent-[#1E4D3B]" />{t("contact_for_price")}</label>}
    </div>
  );
  const descField = <textarea data-testid="create-description" value={f.description} onChange={(e) => set({ description: e.target.value })} rows={4} placeholder={t("description")} className={`${field} py-3`} />;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-32 space-y-6" data-testid="create-page">
      <div className="flex items-center gap-3">
        <button data-testid="create-back" aria-label={t("back")} onClick={() => (step > 1 ? setStep(step - 1) : id ? navigate(-1) : setType(""))} className="press w-11 h-11 rounded-full bg-[#F3EDE4] flex items-center justify-center"><ChevronLeft /></button>
        <h1 className="text-2xl font-extrabold tracking-tight">{id ? t("edit") : t(isReq ? "create_request" : "create_listing")}</h1>
      </div>
      <Progress step={step} />
      <div className="rise space-y-5" key={step}>
        {step === 1 && (isReq ? <>{titleField}{catField}</> : <Photos photos={f.photos} setPhotos={(fn) => setF((s) => ({ ...s, photos: typeof fn === "function" ? fn(s.photos) : fn }))} />)}
        {step === 2 && (isReq ? <>{descField}{priceField}</> : <>{titleField}{priceField}{catField}{descField}</>)}
        {step === 3 && (<>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="space-y-1"><span className="font-bold text-sm">{t("city_hub")}</span>
              <select data-testid="create-hub" value={f.hub_id} onChange={(e) => set({ hub_id: e.target.value, area_id: "" })} className={field}>{activeHubs.map((h) => <option key={h.id} value={h.id}>{L(h)}</option>)}</select></label>
            <label className="space-y-1"><span className="font-bold text-sm">{t("area")} ({t("optional")})</span>
              <select data-testid="create-area" value={f.area_id} onChange={(e) => set({ area_id: e.target.value })} className={field}><option value="">—</option>{areasOf(f.hub_id).map((a) => <option key={a.id} value={a.id}>{L(a)}</option>)}</select></label>
          </div>
          {id && (
            <label className="space-y-1 block"><span className="font-bold text-sm">{t("status")}</span>
              <select data-testid="create-status" value={f.status} onChange={(e) => set({ status: e.target.value })} className={field}>
                {(isReq ? ["open", "found"] : ["available", "sold"]).map((s) => <option key={s} value={s}>{t(s)}</option>)}
              </select></label>
          )}
          <p className="text-xs text-[#6B5E55]">{t("only_active_hubs")}</p>
          {!isReq && <ShopBox f={f} set={set} hasShop={hasShop} />}
          <div className="space-y-3" data-testid="live-preview">
            <div><div className="font-extrabold text-lg">{t("preview")}</div><div className="text-sm text-[#6B5E55]">{t("preview_hint")}</div></div>
            <div className="max-w-sm"><PostCard p={preview} preview /></div>
            {preview.description && <div className="rounded-2xl bg-[#FFFDF9] border border-[#E8E2D9] p-4"><div className="font-bold mb-1">{preview.title}</div><p className="text-sm whitespace-pre-line text-[#291E18]">{preview.description}</p></div>}
          </div>
        </>)}
      </div>
      <div className="fixed bottom-[calc(72px+env(safe-area-inset-bottom))] inset-x-0 z-30 px-4">
        <div className="max-w-2xl mx-auto">
          {step < 3 ? <button data-testid="create-next" onClick={next} className="press w-full min-h-[56px] rounded-full bg-[#1E4D3B] text-white font-extrabold text-lg shadow-xl">{t("next")}</button>
            : <button data-testid="create-publish" disabled={busy} onClick={publish} className="press w-full min-h-[56px] rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold text-lg shadow-xl disabled:opacity-60">{busy ? t("publishing") : id ? t("save_changes") : t("publish")}</button>}
        </div>
      </div>
    </div>
  );
}
